/*
 * PROD-7828 D110 — pure request builders and enums for month close/reopen runs. Every enum mirrors
 * goserver month_run_types.go; params.test.js pins them against __fixtures__/month_run_enums.json and
 * request_fields.test.js pins each builder against the fields its handler reads (CD-139).
 */
import { READINESS_STATES, trim_reason } from '../params';

export const RUN_KIND_CLOSE = 'CLOSE';
export const RUN_KIND_REOPEN = 'REOPEN';
export const RUN_KINDS = [RUN_KIND_CLOSE, RUN_KIND_REOPEN];

export const RUN_STATUSES = ['QUEUED', 'RUNNING', 'PAUSED', 'COMPLETED', 'CANCELLED'];
export const ACTIVE_RUN_STATUSES = ['QUEUED', 'RUNNING', 'PAUSED'];
export const RUN_PAUSED_REASONS = ['SWITCH_OFF', 'FAILING', 'STARTER_NO_ACCESS'];

export const RUN_ITEM_STATUSES = [
  'PENDING',
  'RUNNING',
  'CLOSED',
  'REOPENED',
  'ALREADY_DONE',
  'REFUSED',
  'FAILED',
  'UNKNOWN',
  'NOT_ATTEMPTED'
];

// The server's bucket list; the panel offers them "Needs attention" first (RUN_BUCKET_TABS).
export const RUN_BUCKETS = ['ALL', 'NEEDS_ATTENTION', 'DONE', 'PENDING', 'NOT_ATTEMPTED'];
export const BUCKET_NEEDS_ATTENTION = 'NEEDS_ATTENTION';
export const RUN_BUCKET_TABS = ['NEEDS_ATTENTION', 'DONE', 'PENDING', 'NOT_ATTEMPTED', 'ALL'];

export const SELECTION_LIST = 'LIST';
export const SELECTION_FILTER = 'FILTER';
export const SELECTION_MODES = [SELECTION_LIST, SELECTION_FILTER];

// monthRunFilterStates: a close snapshots open months, a reopen closed ones.
export const RUN_FILTER_STATES = {
  CLOSE: ['READY', 'BLOCKED'],
  REOPEN: ['CLOSED']
};

// month_run_types.go limits.
export const MAX_RUN_TEACHERS = 2000;
export const RUN_ITEMS_PAGE = 50;
export const MAX_RUN_ITEMS_PAGE = 100;
export const RUNS_PAGE = 20;
export const MAX_RUNS_PAGE = 50;

// D110 §15: poll every 3 s while the page is visible, every 15 s while it is hidden. A PAUSED run
// changes only when a person resumes it, so it backs off to the hidden rate too.
export const POLL_VISIBLE_MS = 3000;
export const POLL_HIDDEN_MS = 15000;
export const poll_delay_ms = (hidden, status = '') =>
  hidden || status === 'PAUSED' ? POLL_HIDDEN_MS : POLL_VISIBLE_MS;

const in_list = (list, value) => list.indexOf(value) !== -1;

export const is_run_active = (status) => in_list(ACTIVE_RUN_STATUSES, status);

const whole = (n, fallback) => {
  const v = Math.floor(Number(n));
  return Number.isFinite(v) ? v : fallback;
};

const clamp_limit = (limit, fallback, max) => Math.min(Math.max(whole(limit, fallback), 1), max);

const unique_uuids = (uuids) =>
  (Array.isArray(uuids) ? uuids : [])
    .map((u) => String(u || '').trim())
    .filter((u, i, arr) => u && arr.indexOf(u) === i);

// ---- selections ----

export const list_selection = (uuids) => ({
  mode: SELECTION_LIST,
  teacher_uuids: unique_uuids(uuids).slice(0, MAX_RUN_TEACHERS)
});

// "Select all N matching": the readiness filter plus the N Ops confirmed (SELECTION_CHANGED guards it).
export const filter_selection = (state, q, expected_count) => ({
  mode: SELECTION_FILTER,
  state: in_list(READINESS_STATES, state) ? state : '',
  q: String(q === null || q === undefined ? '' : q).trim(),
  expected_count: Math.max(whole(expected_count, 0), 0)
});

export const selection_count = (selection) => {
  const s = selection || {};
  if (s.mode === SELECTION_FILTER) return Math.max(whole(s.expected_count, 0), 0);
  return Array.isArray(s.teacher_uuids) ? s.teacher_uuids.length : 0;
};

// The server refuses the other mode's fields (BAD_SELECTION), so each mode sends only its own.
const selection_body = (selection) => {
  const s = selection || {};
  if (s.mode === SELECTION_FILTER) {
    const f = filter_selection(s.state, s.q, s.expected_count);
    return { mode: f.mode, state: f.state, q: f.q, expected_count: f.expected_count };
  }
  return list_selection(s.teacher_uuids);
};

// ---- request builders ----

// POST /month/runs. override_reason is the D33 second reason and belongs to a close only;
// retry_of_run_uuid names the finished run whose failed teachers this one retries ('' otherwise).
export const run_create_payload = ({
  kind,
  pay_month,
  reason,
  override_reason,
  client_request_id,
  selection,
  retry_of_run_uuid
}) => ({
  kind: in_list(RUN_KINDS, kind) ? kind : '',
  pay_month: pay_month || '',
  reason: trim_reason(reason),
  override_reason: kind === RUN_KIND_CLOSE ? trim_reason(override_reason) : '',
  client_request_id: client_request_id || '',
  selection: selection_body(selection),
  retry_of_run_uuid: String(retry_of_run_uuid || '').trim()
});

export const run_params = (run_uuid, bucket = 'ALL', limit = RUN_ITEMS_PAGE, offset = 0) => ({
  run_uuid: run_uuid || '',
  bucket: in_list(RUN_BUCKETS, bucket) ? bucket : 'ALL',
  limit: clamp_limit(limit, RUN_ITEMS_PAGE, MAX_RUN_ITEMS_PAGE),
  offset: Math.max(whole(offset, 0), 0)
});

export const runs_params = (pay_month, limit = RUNS_PAGE, offset = 0) => ({
  pay_month: pay_month || '',
  limit: clamp_limit(limit, RUNS_PAGE, MAX_RUNS_PAGE),
  offset: Math.max(whole(offset, 0), 0)
});

export const run_cancel_payload = (run_uuid, reason) => ({
  run_uuid: run_uuid || '',
  reason: trim_reason(reason)
});

// expected_takeover: the page showed "Resume as you"; the server refuses if its decision differs (RESUME_TAKEOVER_CHANGED).
export const run_resume_payload = (run_uuid, expected_takeover = false) => ({
  run_uuid: run_uuid || '',
  expected_takeover: expected_takeover === true
});

// ---- "Retry the teachers that failed" (B8) ----

const item_result = (item) => (item && (item.close_result || item.reopen_result)) || null;

export const item_blockers = (item) => {
  const r = item_result(item);
  return r && Array.isArray(r.blockers) ? r.blockers.filter(Boolean) : [];
};

// CD-269: the server decides which teachers a retry may re-pick (CD-91, CD-108); a missing flag is false.
export const is_retryable_item = (item) => !!item && item.retryable === true;

// L7: a run is a retry of the seeded one only while it covers a subset of the seeded teachers (no "select all
// matching", nobody added); otherwise it is a new run and names none.
export const retry_of_run = (seed, selected_uuids, match_all) => {
  if (!seed || !seed.run_uuid || match_all) return '';
  const seeded = new Set((Array.isArray(seed.rows) ? seed.rows : []).map((r) => r.teacher_uuid));
  const picked = Array.isArray(selected_uuids) ? selected_uuids : [];
  return picked.length > 0 && picked.every((u) => seeded.has(u)) ? seed.run_uuid : '';
};

// The rows a retry run seeds its dialog with: the shape needs_override_reason and the dialogs read.
export const retry_rows = (items) =>
  (Array.isArray(items) ? items : []).filter(is_retryable_item).map((item) => ({
    teacher_uuid: item.teacher_uuid,
    teacher_name: item.teacher_name || item.teacher_uuid,
    blockers: item_blockers(item)
  }));
